import { useCallback, useEffect, useMemo, useRef } from 'react'

import { Skeleton, Tooltip } from '@mantine/core'

import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_TERMINATION_REASONS } from 'src/graphql/retentionMetrics.graphql'
import { TERMINATED_REASON_LABEL_OVERRIDES } from 'src/lib/terminatedReason.utils'
import { cn } from 'src/utils'

import AnalyticsCard from '../../components/AnalyticsCard'

import type {
  StatusLogFiltersInput,
  TerminatedReason,
  TerminationBucket,
} from 'types/graphql'

const INFO_TEXT = [
  'What caused each termination, split into voluntary (the hire chose to leave) and involuntary (the company let them go).',
  'Voluntary turnover usually points to retention factors you can adjust — pay, schedule, working conditions. A jump here is a market signal.',
  'Involuntary turnover usually points to hiring-fit and operations — failed screens, safety, attendance. A jump here usually means recruiting or onboarding need to tighten.',
  'Voluntary includes: quits, retirements, and medical-related terminations.',
].join('\n\n')

export interface TerminationReasonRow {
  reason: TerminatedReason
  bucket: TerminationBucket
  count: number
}

/**
 * Convert an enum value like `PERFORMANCE_ISSUES` to sentence case
 * ("Performance issues"). Only the first character is capitalized, the rest
 * are lowercased — matches the playground sketch. Not equivalent to
 * `formatSnakeValue` from `src/lib/formatters` which produces Title Case.
 */
export const formatReasonLabel = (reason: string): string => {
  if (!reason) return reason
  // Honor display-label overrides (e.g. VIOLATION_OF_COMPANY_POLICY →
  // "Company Policy") so this card matches the dropdowns; the override is
  // sentence-cased to fit the card's style.
  const source =
    TERMINATED_REASON_LABEL_OVERRIDES[reason as TerminatedReason] ?? reason
  const lower = source.toLowerCase().replace(/_/g, ' ').trim()
  if (lower.length === 0) return lower
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

export interface TerminationReasonsCardViewProps {
  rows: ReadonlyArray<TerminationReasonRow>
  totalTerminated: number
  voluntaryCount: number
  involuntaryCount: number
  loading?: boolean
}

const formatPct = (count: number, total: number): string => {
  if (total === 0) return '0%'
  return `${Math.round((count / total) * 100)}%`
}

export const TerminationReasonsCardView = ({
  rows,
  totalTerminated,
  voluntaryCount,
  involuntaryCount,
  loading = false,
}: TerminationReasonsCardViewProps) => {
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => b.count - a.count),
    [rows]
  )

  const maxCount = useMemo(
    () => sortedRows.reduce((m, r) => (r.count > m ? r.count : m), 0),
    [sortedRows]
  )

  if (loading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton height={40} radius="md" />
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={14} radius="sm" />
          ))}
        </div>
      </div>
    )
  }

  if (totalTerminated === 0) {
    return (
      <div className="px-4 py-8 text-center text-xs text-doubleNickel-gray-500">
        No terminations in this period.
      </div>
    )
  }

  const voluntaryWidthPct = (voluntaryCount / totalTerminated) * 100
  const involuntaryWidthPct = (involuntaryCount / totalTerminated) * 100

  return (
    <div className="flex flex-col gap-4">
      {/* Voluntary vs involuntary split */}
      <div>
        <div className="mb-1.5 flex items-baseline justify-between text-xs">
          <span className="text-doubleNickel-gray-600">
            Voluntary vs involuntary
          </span>
          <span
            className="font-mono tabular-nums text-doubleNickel-gray-700"
            data-testid="termination-total"
          >
            {totalTerminated.toLocaleString()} terminations
          </span>
        </div>

        <div
          className="flex h-3 overflow-hidden rounded-full bg-doubleNickel-gray-100"
          data-testid="termination-stacked-bar"
        >
          {voluntaryCount > 0 && (
            <Tooltip
              label={`Voluntary: ${voluntaryCount} (${voluntaryWidthPct.toFixed(1)}%)`}
              withArrow
              position="top"
            >
              <div
                className="h-full bg-doubleNickel-success-600"
                style={{ width: `${voluntaryWidthPct}%` }}
                data-testid="termination-bar-voluntary"
                data-width-pct={voluntaryWidthPct.toFixed(2)}
              />
            </Tooltip>
          )}
          {involuntaryCount > 0 && (
            <Tooltip
              label={`Involuntary: ${involuntaryCount} (${involuntaryWidthPct.toFixed(1)}%)`}
              withArrow
              position="top"
            >
              <div
                className="h-full bg-doubleNickel-error-600"
                style={{ width: `${involuntaryWidthPct}%` }}
                data-testid="termination-bar-involuntary"
                data-width-pct={involuntaryWidthPct.toFixed(2)}
              />
            </Tooltip>
          )}
        </div>

        <div className="mt-2 flex gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-doubleNickel-success-600" />
            <span className="text-doubleNickel-gray-600">Voluntary</span>
            <span
              className="font-mono font-semibold tabular-nums text-doubleNickel-gray-800"
              data-testid="termination-legend-voluntary"
            >
              {voluntaryCount} ({formatPct(voluntaryCount, totalTerminated)})
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-doubleNickel-error-600" />
            <span className="text-doubleNickel-gray-600">Involuntary</span>
            <span
              className="font-mono font-semibold tabular-nums text-doubleNickel-gray-800"
              data-testid="termination-legend-involuntary"
            >
              {involuntaryCount} ({formatPct(involuntaryCount, totalTerminated)})
            </span>
          </div>
        </div>
      </div>

      {/* Per-reason rows */}
      <div className="flex flex-col gap-1.5">
        {sortedRows.map((row) => {
          const pctOfTotal = (row.count / totalTerminated) * 100
          const widthPct = maxCount > 0 ? (row.count / maxCount) * 100 : 0
          const isVoluntary = row.bucket === 'VOLUNTARY'
          const colorClass = isVoluntary
            ? 'bg-doubleNickel-success-600'
            : 'bg-doubleNickel-error-600'
          return (
            <div
              key={row.reason}
              className="grid grid-cols-12 items-center gap-2 text-xs"
              data-testid={`reason-row-${row.reason}`}
            >
              <div className="col-span-5 flex min-w-0 items-center gap-2">
                <span
                  className={cn(
                    'inline-block h-2 w-2 flex-shrink-0 rounded-sm',
                    colorClass
                  )}
                />
                <span className="truncate font-medium text-doubleNickel-gray-800">
                  {formatReasonLabel(row.reason)}
                </span>
              </div>
              <div className="col-span-5">
                <div className="h-1.5 rounded-full bg-doubleNickel-gray-100">
                  <div
                    className={cn('h-full rounded-full opacity-70', colorClass)}
                    style={{ width: `${widthPct}%` }}
                    data-testid={`reason-bar-${row.reason}`}
                    data-width-pct={Math.round(widthPct)}
                  />
                </div>
              </div>
              <div
                className="col-span-1 text-right font-mono font-semibold tabular-nums text-doubleNickel-gray-800"
                data-testid={`reason-count-${row.reason}`}
              >
                {row.count}
              </div>
              <div className="col-span-1 text-right font-mono tabular-nums text-doubleNickel-gray-500">
                {Math.round(pctOfTotal)}%
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export interface TerminationReasonsCardProps {
  filters: StatusLogFiltersInput
  /**
   * Bumped by the page-level Refresh button — when this value changes (after
   * the initial mount), the card calls its own `refetch()` to re-pull data
   * with the same filters.
   */
  refreshKey: number
}

const TerminationReasonsCard = ({
  filters,
  refreshKey,
}: TerminationReasonsCardProps) => {
  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(GET_TERMINATION_REASONS, {
    variables: { filters },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  const isFirstRefreshKey = useRef(true)
  useEffect(() => {
    if (isFirstRefreshKey.current) {
      isFirstRefreshKey.current = false
      return
    }
    refetch()
  }, [refreshKey, refetch])

  const result = data?.terminationReasons
  const rows = (result?.rows ?? []) as ReadonlyArray<TerminationReasonRow>

  return (
    <AnalyticsCard title="Termination Reasons" info={INFO_TEXT}>
      <TerminationReasonsCardView
        rows={rows}
        totalTerminated={result?.totalTerminated ?? 0}
        voluntaryCount={result?.voluntaryCount ?? 0}
        involuntaryCount={result?.involuntaryCount ?? 0}
        loading={loading && !data}
      />
    </AnalyticsCard>
  )
}

export default TerminationReasonsCard
